import { TEAM_EMAIL_DOMAIN } from "@dream/config/constants";
import { Button, IconAlertCircle, IconGoogle } from "@dream/ui";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { ApiError, fetchAuthOptions, signInWithGoogle } from "../api/client.ts";
import { LegalNav } from "../components/cookie-consent.tsx";
import { DreamLogo } from "../components/dream-logo.tsx";
import { ThemeToggle } from "../components/theme-toggle.tsx";
import { TypeIcon } from "../components/type-icon.tsx";
import { loginRoute } from "../router.tsx";

/**
 * Frame Login do Ofative UI Kit (135:33488), 1440×960.
 *
 * Esquerda / direita `#0074FF`. Entrada só pelo Google, e só
 * `@dreamvision.com.br`. Sem cadastro público e sem senha.
 */

function loginErrorMessage(code: string | undefined): string | null {
  if (code === undefined || code === "") return null;
  if (code === "restricted" || code === "unable_to_create_user" || code === "signup_disabled") {
    return `Acesso restrito a contas @${TEAM_EMAIL_DOMAIN}.`;
  }
  if (code === "access_denied") {
    return "O login com Google foi cancelado.";
  }
  return `Não foi possível entrar com o Google. Use uma conta @${TEAM_EMAIL_DOMAIN}.`;
}

export function LoginPage() {
  const search = useSearch({ from: loginRoute.id });
  const [serverError, setServerError] = useState<string | null>(() =>
    loginErrorMessage(search.error),
  );
  const [submitting, setSubmitting] = useState(false);

  const options = useQuery({
    queryKey: ["auth-options"],
    queryFn: fetchAuthOptions,
    staleTime: 60_000,
  });

  const googleReady = options.data?.google === true;
  const allowedDomain = options.data?.allowedDomain ?? TEAM_EMAIL_DOMAIN;

  async function onGoogle(): Promise<void> {
    setServerError(null);
    if (!googleReady) {
      setServerError(
        "O login com Google ainda não está configurado neste ambiente. Defina GOOGLE_CLIENT_ID e GOOGLE_CLIENT_SECRET.",
      );
      return;
    }

    setSubmitting(true);
    try {
      const path =
        typeof search.redirect === "string" && search.redirect.startsWith("/")
          ? (search.redirect.split("?")[0] ?? "/calendario")
          : "/calendario";
      await signInWithGoogle(path);
    } catch (error) {
      setSubmitting(false);
      setServerError(
        error instanceof ApiError ? error.message : "Não foi possível iniciar o login com Google.",
      );
    }
  }

  return (
    <div className="flex h-full min-h-0 overflow-x-clip bg-[var(--color-surface)]">
      <section className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto px-6 py-8 pt-[max(2rem,env(safe-area-inset-top))] sm:px-10 sm:py-10 lg:px-[50px] lg:py-[50px]">
        <div className="flex items-start justify-between gap-4">
          <DreamLogo size="lg" />
          <ThemeToggle compact />
        </div>

        <div className="dream-enter mx-auto flex w-full max-w-[551px] flex-1 flex-col justify-center">
          <h1 className="text-center text-[24px] font-bold leading-[30px] tracking-[0] text-[var(--color-ink)] sm:text-[28px] sm:leading-[34px]">
            Bem-vindo de volta!
          </h1>
          <p className="mx-auto mt-3 max-w-[551px] text-center text-[16px] font-normal leading-[24px] text-[var(--color-ink-secondary)]">
            O Dream Calendar reúne clientes, campanhas e entregas num só calendário — a equipe
            opera, o cliente acompanha.
          </p>

          <div className="mt-8 space-y-5">
            {serverError !== null && (
              <div
                role="alert"
                className="dream-enter flex items-start gap-2 rounded-lg border border-[#F99696] bg-[#FEEDED] px-3 py-2.5 text-[14px] font-medium text-[#AB3434]"
              >
                <IconAlertCircle className="mt-0.5 size-4 shrink-0" />
                <span>{serverError}</span>
              </div>
            )}

            <Button
              type="button"
              variant="secondary"
              size="lg"
              loading={submitting || options.isLoading}
              className="dream-float h-11 w-full rounded-lg border border-[var(--color-line)] text-[16px] font-semibold"
              onClick={() => void onGoogle()}
            >
              <IconGoogle className="size-5" />
              Entrar com @{allowedDomain}
            </Button>

            <p className="text-center text-[14px] font-medium leading-5 text-[var(--color-ink-muted)]">
              O Google lista só contas @{allowedDomain}. No mesmo passo, autorize a Agenda completa
              — o calendário lê, cria e edita eventos na sua conta. Gmail pessoal não entra.
            </p>
            {!googleReady && !options.isLoading && (
              <p className="text-center text-[13px] leading-5 text-[var(--color-ink-muted)]">
                O login com Google ainda não está configurado neste ambiente. Defina
                GOOGLE_CLIENT_ID e GOOGLE_CLIENT_SECRET.
              </p>
            )}
          </div>

          <LegalNav className="mt-8 justify-center" />
        </div>
      </section>

      <aside
        className="relative hidden h-full w-[40.9%] min-w-[420px] max-w-[589px] overflow-hidden lg:flex lg:flex-col"
        style={{ backgroundColor: "#0074FF" }}
        aria-hidden="true"
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.14]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "96px 96px",
          }}
        />

        <div className="dream-enter relative z-10 px-10 pt-24" style={{ animationDelay: "80ms" }}>
          <p className="max-w-[509px] text-[40px] font-bold leading-[50px] tracking-[0] text-white">
            Redefina sua experiência de calendário!
          </p>
          <p className="mt-3 max-w-[509px] text-[16px] font-medium leading-[24px] text-white">
            Todas as entregas do cliente no mesmo quadro: postagens, captações, edições, aprovações
            e prazos.
          </p>
        </div>

        <div
          className="dream-enter relative z-10 mt-8 flex flex-1 items-end px-6 pb-16"
          style={{ animationDelay: "140ms" }}
        >
          <LoginHeroCalendar />
        </div>

        <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 gap-2">
          <span className="size-2.5 rounded-full bg-white" />
          <span className="size-2.5 rounded-full bg-white/40" />
          <span className="size-2.5 rounded-full bg-white/40" />
        </div>
      </aside>
    </div>
  );
}

function LoginHeroCalendar() {
  const days = [
    { n: "10", d: "Qua" },
    { n: "11", d: "Qui" },
    { n: "12", d: "Sex", today: true },
    { n: "13", d: "Sáb" },
  ];

  return (
    <div
      className="w-full origin-bottom-left overflow-hidden rounded-[12px] bg-white shadow-[0_24px_48px_-12px_rgb(0_0_0/0.28)]"
      style={{ transform: "perspective(1400px) rotateY(-12deg) rotateX(4deg) scale(1.08)" }}
    >
      <div className="flex h-12 items-center justify-between border-b border-[#E7E7EC] px-4">
        <span className="text-[14px] font-semibold text-[#1C1C1C]">Setembro 2026</span>
        <span className="rounded-md bg-[#FBFBFC] px-2 py-1 text-[12px] font-bold text-[#0074FF]">
          Semanal
        </span>
      </div>
      <div className="grid grid-cols-4 border-b border-[#E7E7EC]">
        {days.map((day) => (
          <div key={day.n} className="px-3 py-2">
            <p
              className={`text-[20px] font-bold leading-[25px] ${day.today ? "text-[#0074FF]" : "text-[#1C1C1C]"}`}
            >
              {day.n}
            </p>
            <p
              className={`text-[14px] font-semibold ${day.today ? "text-[#0074FF]" : "text-[#5C6370]"}`}
            >
              {day.d}
            </p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2 p-3">
        <HeroChip label="Reels" time="08:00 – 09:00" color="#F9DCD3" type="reels" delay={0} />
        <HeroChip
          label="Captação"
          time="09:00 – 12:00"
          color="#E6DBF4"
          type="shooting"
          delay={40}
        />
        <HeroChip label="Sprint" time="08:00 – 09:00" color="#D0E5FF" type="meeting" delay={80} />
        <HeroChip label="Edição" time="10:00 – 13:00" color="#BFDEDD" type="editing" delay={120} />
        <HeroChip label="Reunião" time="10:00 – 13:00" color="#D0E5FF" type="meeting" delay={160} />
        <HeroChip
          label="Aprovação"
          time="14:00 – 15:00"
          color="#DBECDD"
          type="approval"
          delay={200}
        />
        <HeroChip
          label="Publicação"
          time="12:00 – 15:00"
          color="#F9DCD3"
          type="publication"
          delay={240}
        />
        <HeroChip
          label="Relatório"
          time="11:00 – 14:00"
          color="#DBECDD"
          type="report"
          delay={280}
        />
      </div>
    </div>
  );
}

function HeroChip({
  label,
  time,
  color,
  type,
  delay,
}: {
  label: string;
  time: string;
  color: string;
  type: string;
  delay: number;
}) {
  return (
    <div
      className="rounded-lg px-2 py-2"
      style={{
        backgroundColor: color,
        animation: "dream-chip-in var(--dream-duration-base) var(--dream-ease-out) both",
        animationDelay: `${delay}ms`,
      }}
    >
      <div className="flex items-center gap-1.5">
        <TypeIcon slug={type} size={13} />
        <p className="truncate text-[12px] font-semibold text-[#1C1C1C]">{label}</p>
      </div>
      <p className="mt-0.5 text-[10px] font-semibold tabular-nums text-[#3F3F46]">{time}</p>
    </div>
  );
}
